// src/components/UnactivatedOffer.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandMore } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Offer } from "../../lib/types/detailsTypes";
import { activateOffer, deleteOffer } from "./offersFirestore";
import NearbyOfferLocationsButton from "../../reusables/nearbyLocationsButton";
// ^ Adjust paths as needed

interface UnactivatedOfferProps {
  open: boolean;
  offer: Offer;
  onClose: () => void;
  onActivate?: () => void;
  userId: string;
  onOfferDeleted?: (offerId: string) => void;
}

const UnactivatedOffer: React.FC<UnactivatedOfferProps> = ({
  open,
  offer,
  onClose,
  onActivate,
  userId,
  onOfferDeleted,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Control loading states for both activation and deletion
  const [isProcessing, setIsProcessing] = useState(false);

  // Confirmation dialogs for activation and deletion
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Calculate if offer is expired
  const now = new Date();
  const validFrom = offer.validFrom ? offer.validFrom.toDate() : null;
  const validUntil = offer.validUntil ? offer.validUntil.toDate() : null;
  const isExpiredByDateRange =
    validFrom && validUntil ? now < validFrom || now > validUntil : false;
  const isExpired = isExpiredByDateRange || offer.isExpired;

  /** Handles closing the main offer dialog's snackbar. */
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  /** Opens the "Confirm Activate" dialog. */
  const handleOpenActivateDialog = () => {
    setActivateDialogOpen(true);
  };

  /** Closes the "Confirm Activate" dialog. */
  const handleCloseActivateDialog = () => {
    setActivateDialogOpen(false);
  };

  /** Final step: user confirmed activation. */
  const handleConfirmActivate = async () => {
    setIsProcessing(true);
    setActivateDialogOpen(false);

    const { success, message } = await activateOffer(offer.id);
    setSnackbarMessage(message);
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);

    // If activation succeeded, notify parent so it can refresh
    if (success && onActivate) {
      onActivate();
    }

    setIsProcessing(false);
  };

  /** Opens the "Confirm Delete" dialog. */
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  /** Closes the "Confirm Delete" dialog. */
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  /** Final step: user confirmed deletion. */
  const handleDeleteConfirm = async () => {
    setIsProcessing(true);
    setDeleteDialogOpen(false);

    const { success, message } = await deleteOffer(offer.id);
    setSnackbarMessage(message);
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);

    if (success) {
      // Notify parent component about deletion
      onOfferDeleted?.(offer.id);
      // Close the offer dialog after successful deletion
      onClose();
    }

    setIsProcessing(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 400,
            maxHeight: "80vh",
            borderRadius: 4,
            position: "relative",
            m: "auto",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{ position: "relative" }}>
          {offer.storeName ? offer.storeName : "Offer Details"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              backgroundColor: "white",
              color: "black",
              borderRadius: "50%",
              boxShadow: 2,
              zIndex: 1,
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent
          sx={{
            overflowY: "auto",
            flex: 1,
            px: 2,
            pt: 1,
            pb: 2,
          }}
        >
          {offer.imageUrl && (
            <Box
              component="img"
              src={offer.imageUrl}
              alt={offer.title}
              sx={{
                width: "100%",
                maxHeight: 160,
                objectFit: "contain",
                borderRadius: 2,
                mb: 2,
                mt: 1,
              }}
            />
          )}
          <Divider sx={{ mb: 2 }} />
          {isExpired && (
            <Chip label="Expired" color="error" size="small" sx={{ mb: 2 }} />
          )}
          <Typography variant="h6" gutterBottom sx={{ fontSize: "1.5rem" }}>
            <strong>{offer.title}</strong>
          </Typography>
          <Typography variant="body1" paragraph>
            {offer.description}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {offer.singleUse ? "1 per Customer Only" : "Multiple uses allowed"}
          </Typography>
          {offer.validUntil && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Expires: {offer.validUntil.toDate().toLocaleDateString()}
            </Typography>
          )}
          {offer.termsAndConditions && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Accordion disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="terms-content"
                  id="terms-header"
                >
                  <Typography variant="subtitle2">Terms</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary">
                    {offer.termsAndConditions}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
          sx={{
            flexDirection: "column",
            alignItems: "stretch",
            px: 2,
            position: "relative",
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              p: 1,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <NearbyOfferLocationsButton
              storeId={offer.storeId}
              acceptedLocationIds={offer.acceptedLocations}
            />
            <Button
              onClick={handleOpenActivateDialog}
              variant="contained"
              color="success"
              disabled={isExpired || offer.isActivated || isProcessing}
              sx={{
                mt: 1,
                fontWeight: "bold",
                flex: 1,
                borderRadius: "999px",
                boxShadow: 3,
                py: 1.5,
                height: 40,
              }}
            >
              {offer.isActivated ? "Activated" : "Activate Offer"}
            </Button>
            <IconButton
              onClick={openDeleteDialog}
              disabled={offer.isActivated || isProcessing}
              sx={{
                mt: 1,
                width: 40,
                height: 40,
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: 3,
                p: 1,
              }}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirm Activation Dialog */}
      <Dialog open={activateDialogOpen} onClose={handleCloseActivateDialog}>
        <DialogTitle>
          <strong>Confirm Activation</strong>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            The offer will <strong>expire in 10 minutes</strong> after
            activation, and you must show the activated screen to the cashier to
            claim it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateDialog} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmActivate}
            variant="contained"
            color="success"
            disabled={isProcessing}
          >
            Yes, Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deletion Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this offer? This action is
            irreversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isProcessing}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UnactivatedOffer;
